import { Edit } from '@mui/icons-material';
import {
  Checkbox,
  IconButton,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { memo } from 'react';
import { InView } from 'react-intersection-observer';

import { OrganizationMember } from '@/types';

import { MemberState } from './MemberState';
import { UserFriendlyDateWithTooltip } from './UserFriendlyDateWithTooltip';

interface MemberRowProps {
  toggleSelectMember: (organizationMemberId: number) => void;
  isSelected: boolean;
  isDisabled: boolean;
  isManagedOrg: boolean;
  organizationMemberId: OrganizationMember['organizationMemberId'];
  name: OrganizationMember['name'];
  organizationEmail: OrganizationMember['organizationEmail'];
  userEmail: OrganizationMember['userEmail'];
  state: OrganizationMember['state'];
  lastActivityDate: OrganizationMember['lastActivityDate'];
  lastActivityType: OrganizationMember['lastActivityType'];
  organizationMemberCreatedAt: OrganizationMember['organizationMemberCreatedAt'];
  openEditModal?: (id: OrganizationMember['organizationMemberId']) => void;
}

export const MemberRow = memo(function MemberRow({
  organizationMemberId,
  toggleSelectMember,
  isDisabled,
  isSelected,
  isManagedOrg,
  name,
  organizationEmail,
  userEmail,
  state,
  lastActivityDate,
  lastActivityType,
  organizationMemberCreatedAt,
  openEditModal,
}: MemberRowProps) {
  return (
    <InView threshold={0} rootMargin="1000px 0px">
      {({ inView, ref }) => (
        <TableRow
          ref={ref}
          key={organizationMemberId}
          sx={{
            '&:last-child td, &:last-child th': { border: 0 },
            '.MuiIconButton-root': { opacity: 0 },
            '&:hover .MuiIconButton-root': { opacity: 1 },
          }}>
          {!inView ? (
            <TableCell sx={{ height: 81 }} />
          ) : (
            <>
              <TableCell
                padding="checkbox"
                onClick={
                  !isDisabled
                    ? () => toggleSelectMember(organizationMemberId)
                    : undefined
                }
                role="checkbox"
                aria-label={`Select ${name}`}
                aria-checked={isSelected}
                tabIndex={-1}
                sx={
                  !isDisabled
                    ? {
                        cursor: 'pointer',
                        '&:hover .MuiCheckbox-root': {
                          backgroundColor: 'rgba(0, 169, 178, 0.04)',
                        },
                        width: '5%',
                      }
                    : { width: '5%' }
                }>
                <Checkbox
                  color="primary"
                  disabled={isDisabled}
                  checked={isSelected}
                  slotProps={{
                    input: { 'aria-label': `Select ${name}` },
                  }}
                />
              </TableCell>
              <TableCell
                component="th"
                scope="row"
                sx={{ paddingBlock: 2, width: openEditModal ? '35%' : '40%' }}>
                <Typography variant="body2SemiBold">{name}</Typography>
                <Typography variant="body3">{organizationEmail}</Typography>
                {userEmail && organizationEmail !== userEmail && (
                  <Typography variant="body3">
                    {userEmail}{' '}
                    <Typography
                      variant="body3"
                      component="span"
                      color="text.light">
                      (login email)
                    </Typography>
                  </Typography>
                )}
              </TableCell>
              <TableCell sx={{ paddingBlock: 2, width: '15%' }}>
                <MemberState
                  state={state}
                  isManagedOrg={isManagedOrg}
                  organizationMemberId={organizationMemberId}
                />
              </TableCell>
              <TableCell sx={{ paddingBlock: 2, width: '25%' }}>
                <UserFriendlyDateWithTooltip
                  activityType={lastActivityType}
                  date={lastActivityDate}
                />
              </TableCell>
              <TableCell sx={{ paddingBlock: 2, width: '15%' }}>
                <Typography variant="body3">
                  {format(organizationMemberCreatedAt, 'MMM d, yyyy')}
                </Typography>
              </TableCell>
              {openEditModal && (
                <TableCell sx={{ padding: 0, width: '5%' }}>
                  <IconButton
                    onClick={() => openEditModal(organizationMemberId)}
                    aria-label="Edit">
                    <Edit sx={{ color: 'secondary.main' }} />
                  </IconButton>
                </TableCell>
              )}
            </>
          )}
        </TableRow>
      )}
    </InView>
  );
});
